import React, { useState, useEffect, useRef } from 'react';

const plans = [
	{
		name: 'Unit',
		description:
			'For companies that want to get a single data platform unit up-and-running for quick insights in their data.',
		features: [
			{ label: 'Data Storage', included: true },
			{ label: 'Data Flows', included: true },
			{ label: 'Data Insights', included: true },
			{ label: 'Data Agentic Mesh', included: false },
			{ label: 'Data Monitoring', included: false },
			{ label: 'Data Infra Security', included: false },
		],
		highlighted: false,
	},
	{
		name: 'Mesh',
		description:
			'For companies that are serious about building a scalable Data Lakehouse Platform with multiple data teams.',
		features: [
			{ label: 'Data Storage', included: true },
			{ label: 'Data Flows', included: true },
			{ label: 'Data Insights', included: true },
			{ label: 'Data Agentic Mesh', included: true },
			{ label: 'Data Monitoring', included: true },
			{ label: 'Data Infra Security', included: false },
		],
		highlighted: true,
	},
	{
		name: 'Enterprise',
		description:
			'For companies that build data solutions with strict security and compliance needs.',
		features: [
			{ label: 'Data Storage', included: true },
			{ label: 'Data Flows', included: true },
			{ label: 'Data Insights', included: true },
			{ label: 'Data Agentic Mesh', included: true },
			{ label: 'Data Monitoring', included: true },
			{ label: 'Data Infra Security', included: true },
		],
		highlighted: false,
	},
];

function PricingSection() {
	const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [showTitle, setShowTitle] = useState(false);
  const [showDesc, setShowDesc] = useState(false);
  const [showCards, setShowCards] = useState(false);
  const [cardVisibles, setCardVisibles] = useState(Array(plans.length).fill(false));

  // Intersection Observer for scroll-in animation
  useEffect(() => {
    const observer = new window.IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.3 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // Staggered appearance
  useEffect(() => {
    let titleTimeout, descTimeout, cardsTimeout;
    if (visible) {
      titleTimeout = setTimeout(() => setShowTitle(true), 200);
      descTimeout = setTimeout(() => setShowDesc(true), 500);
      cardsTimeout = setTimeout(() => setShowCards(true), 700);
    }
    return () => {
      clearTimeout(titleTimeout);
      clearTimeout(descTimeout);
      clearTimeout(cardsTimeout);
    };
  }, [visible]);

  useEffect(() => {
    if (showCards) {
      plans.forEach((_, idx) => {
        setTimeout(() => {
          setCardVisibles(prev => {
            const updated = [...prev];
            updated[idx] = true;
            return updated;
          });
        }, idx * 180);
      });
    } else {
      setCardVisibles(Array(plans.length).fill(false));
    }
  }, [showCards]);

	useEffect(() => {
		const handleResize = () => setIsMobile(window.innerWidth < 768);
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	return (
		<section
      id="pricing"
      ref={sectionRef}
      style={{
        ...sectionStyles,
        padding: isMobile ? '32px 0' : '100px 0', // Remove horizontal padding from section
        borderRadius: isMobile ? 12 : sectionStyles.borderRadius,
        margin: isMobile ? '24px auto 0 auto' : sectionStyles.margin,
      }}
		>
			<div style={{
        ...headerWrap,
        margin: isMobile ? '0 auto 24px auto' : headerWrap.margin,
        maxWidth: isMobile ? 340 : headerWrap.maxWidth
      }}>
				<span style={{
            ...subtitle,
            fontSize: isMobile ? 13 : subtitle.fontSize,
            opacity: showTitle ? 1 : 0,
            transform: showTitle ? 'translateY(0)' : 'translateY(40px)',
            transition: 'opacity 0.7s cubic-bezier(.4,2,.6,1), transform 0.7s cubic-bezier(.4,2,.6,1)'
          }}>MOVE TO CLARITY</span>
				<h2 style={{
            ...title,
            fontSize: isMobile ? 20 : title.fontSize,
            opacity: showTitle ? 1 : 0,
            transform: showTitle ? 'translateY(0)' : 'translateY(40px)',
            transition: 'opacity 0.7s cubic-bezier(.4,2,.6,1), transform 0.7s cubic-bezier(.4,2,.6,1)'
          }}>Choose the setup you need</h2>
				<p style={{
            ...desc,
            fontSize: isMobile ? 13 : desc.fontSize,
            opacity: showDesc ? 1 : 0,
            transition: 'opacity 0.7s 0.2s cubic-bezier(.4,2,.6,1)'
          }}>
					Save hundreds of hours of engineering and invest in a head start. Move
					faster and avoid costly mistakes.
				</p>
			</div>
			{/* Add a wrapper for cards with horizontal padding on mobile */}
      <div style={{
        width: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        justifyContent: isMobile ? 'center' : 'flex-start',
      }}>
        <div
          style={{
            ...pricingGrid,
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 0 : pricingGrid.gap,
            alignItems: isMobile ? 'center' : pricingGrid.alignItems,
            width: isMobile ? '100vw' : pricingGrid.width,
            maxWidth: isMobile ? '100vw' : pricingGrid.maxWidth,
            margin: isMobile ? '0 calc(-50vw + 50%)' : pricingGrid.margin,
            padding: isMobile ? '0 20px' : 0, // Add more horizontal space on mobile
            boxSizing: 'border-box',
          }}
        >
          {plans.map((plan, idx) => {
            const cardStyle = {
              ...card,
              ...(plan.highlighted ? highlightedCard : {}),
              margin: isMobile
                ? '0 auto 18px auto'
                : idx === 1
                ? '0 24px'
                : '0',
              minWidth: isMobile ? '92vw' : 320, // Slightly less than 100vw for more side space
              maxWidth: isMobile ? '380px' : 370,
              width: isMobile ? '100%' : undefined,
              boxSizing: 'border-box',
              padding: isMobile ? '18px 8px 18px 8px' : card.padding,
              minHeight: isMobile ? 320 : card.minHeight,
              opacity: cardVisibles[idx] ? 1 : 0,
              transform: cardVisibles[idx] ? 'translateY(0)' : 'translateY(50px)',
              transition: 'opacity 0.9s cubic-bezier(.4,2,.6,1), transform 0.9s cubic-bezier(.4,2,.6,1)'
            };
            return (
              <div key={plan.name} style={cardStyle}>
                <h3
                  style={{
                    ...planTitle,
                    fontSize: isMobile ? 15 : planTitle.fontSize,
                    color: plan.highlighted ? '#fff' : '#18171b',
                  }}
                >
                  {plan.name}
                </h3>
                <p
                  style={{
                    ...planDesc,
                    fontSize: isMobile ? 12 : planDesc.fontSize,
                    color: plan.highlighted ? '#fff' : '#222',
                  }}
                >
                  {plan.description}
                </p>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'center',
                    margin: isMobile ? '18px 0 8px 0' : '32px 0 16px 0',
                  }}
                >
                  <span
                    style={{
                      ...planPrice,
                      fontSize: isMobile ? 22 : planPrice.fontSize,
                      color: plan.highlighted ? '#fff' : '#18171b',
                    }}
                  >
                    {plan.price}
                  </span>
                  <span
                    style={{
                      ...planPriceNote,
                      fontSize: isMobile ? 11 : planPriceNote.fontSize,
                      color: plan.highlighted ? '#fff' : '#222',
                    }}
                  >
                    {plan.priceNote}
                  </span>
                </div>
                <ul
                  style={{
                    ...featureList,
                    fontSize: isMobile ? 12 : featureList.fontSize,
                    color: plan.highlighted ? '#fff' : '#222',
                  }}
                >
                  {plan.features.map((f, i) => (
                    <li
                      key={f.label}
                      style={{
                        ...featureItem,
                        fontSize: isMobile ? 12 : featureItem.fontSize,
                        color: plan.highlighted
                          ? f.included
                            ? '#fff'
                            : '#bdbdbd'
                          : f.included
                          ? '#18171b'
                          : '#bdbdbd',
                        opacity: f.included ? 1 : 0.6,
                        fontWeight: f.included ? 600 : 400,
                      }}
                    >
                      <span
                        style={{
                          ...checkmark,
                          fontSize: isMobile ? 14 : checkmark.fontSize,
                          color: f.included
                            ? plan.highlighted
                              ? '#6666ff'
                              : '#6666ff'
                            : '#bdbdbd',
                        }}
                      >
                        ✔
                      </span>{' '}
                      {f.label}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
		</section>
	);
}

const sectionStyles = {
	textAlign: 'center',
	color: '#18171b',
	width: '100%',
	margin: '0 auto',
	maxWidth: 1200,
	fontFamily: 'Inter, sans-serif', // match FeaturesSection
	fontWeight: 400, // match FeaturesSection
	letterSpacing: 0.01, // subtle, like FeaturesSection
};
const headerWrap = {
	margin: '0 auto 48px auto',
	maxWidth: 700,
};
const subtitle = {
  color: '#702fff',
	fontWeight: 700,
	fontSize: 18,
	letterSpacing: 1.5,
	textTransform: 'uppercase',
	marginBottom: 10,
	display: 'inline-block',
	fontFamily: 'Inter, sans-serif', // match FeaturesSection
};
const title = {
	fontSize: 32, // match FeaturesSection
	fontWeight: 700,
	color: '#18171b',
	margin: '0 0 18px 0',
	lineHeight: 1.15,
	fontFamily: 'Inter, sans-serif', // match FeaturesSection
};
const desc = {
	color: '#222',
	fontSize: 18, // match FeaturesSection
	fontWeight: 300, // match FeaturesSection
	margin: '0 auto 0 auto',
	maxWidth: 600,
	fontFamily: 'Inter, sans-serif', // match FeaturesSection
	lineHeight: 1.6,
	textAlign: 'center',
};
const pricingGrid = {
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'stretch',
	gap: 0,
	width: '100%',
	margin: '0 auto',
	flexWrap: 'wrap',
};
const card = {
	background: '#fff',
	borderRadius: 22,
	boxShadow: '0 4px 32px 0 rgba(0,0,0,0.07)',
	padding: '38px 28px 32px 28px',
	textAlign: 'left',
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'flex-start',
	minHeight: 480,
	border: 'none',
	flex: 1,
	transition:
		'box-shadow 0.2s cubic-bezier(.4,2,.6,1), transform 0.2s cubic-bezier(.4,2,.6,1)',
};
const highlightedCard = {
	background: '#18171b',
	color: '#fff',
	boxShadow: '0 8px 32px 0 rgba(0,0,0,0.13)',
	border: 'none',
	zIndex: 2,
};
const planTitle = {
	fontSize: 20, // match FeaturesSection
	fontWeight: 700,
	margin: 0,
	fontFamily: 'Inter, sans-serif', // match FeaturesSection
};
const planDesc = {
	fontSize: 16, // match FeaturesSection
	fontWeight: 300, // match FeaturesSection
	margin: '10px 0 0 0',
	fontFamily: 'Inter, sans-serif', // match FeaturesSection
	minHeight: 48,
};
const planPrice = {
	fontSize: 38, // slightly smaller for consistency
	fontWeight: 700,
	fontFamily: 'Inter, sans-serif', // match FeaturesSection
	marginRight: 8,
	lineHeight: 1,
};
const planPriceNote = {
	fontSize: 15, // match FeaturesSection
	fontWeight: 400,
	fontFamily: 'Inter, sans-serif', // match FeaturesSection
	opacity: 0.7,
	alignSelf: 'flex-end',
};
const featureList = {
	listStyle: 'none',
	padding: 0,
	margin: '24px 0 0 0',
	fontSize: 16, // match FeaturesSection
	width: '100%',
	fontFamily: 'Inter, sans-serif', // match FeaturesSection
	fontWeight: 400,
};
const featureItem = {
	display: 'flex',
	alignItems: 'center',
	gap: 10,
	marginBottom: 10,
	fontFamily: 'Inter, sans-serif', // match FeaturesSection
	fontWeight: 400,
};
const checkmark = {
	fontWeight: 700,
	fontSize: 18,
	marginRight: 8,
};

export default PricingSection;
